import * as colors from "@theme/themes/ms5/colors";
import * as badges from "@theme/themes/ms5/variants/badges";
import * as buttons from "@theme/themes/ms5/variants/buttons";
import * as forms from "@theme/themes/ms5/variants/forms";
import * as images from "@theme/themes/ms5/variants/images";
import * as layout from "@theme/themes/ms5/variants/layout";
import * as links from "@theme/themes/ms5/variants/links";
import * as modals from "@theme/themes/ms5/variants/modals";
import * as pagination from "@theme/themes/ms5/variants/pagination";
import * as text from "@theme/themes/ms5/variants/text";
import * as tooltips from "@theme/themes/ms5/variants/tooltips";

export const breakpointMap = {
  sm: "480px",
  md: "768px",
  lg: "1024px",
  xl: "1366px",
  "2xl": "1920px",
} as const;
export const breakpoints = Object.values(breakpointMap);
export const mediaQueries = {
  sm: `@media only screen and (min-width: ${breakpointMap.sm})`,
  md: `@media only screen and (min-width: ${breakpointMap.md})`,
  lg: `@media only screen and (min-width: ${breakpointMap.lg})`,
  xl: `@media only screen and (min-width: ${breakpointMap.xl})`,
  "2xl": `@media only screen and (min-width: ${breakpointMap["2xl"]})`,
} as const;

export const fontSizes = {
  fs10: "0.625rem", // 10px
  fs12: "0.75rem", // 12px
  fs14: "0.875rem", // 14px
  fs16: "1rem", // 16px
  fs18: "1.125rem", // 18px
  fs19: "1.1875rem", // 19px
  fs20: "1.25rem", // 20px
  fs24: "1.5rem", // 24px
  fs28: "1.75rem", // 28px
  fs30: "1.875rem", // 30px
  fs32: "2rem", // 32px
  fs36: "2.25rem", // 36px
  fs40: "2.5rem", // 40px
  fs44: "2.75rem", // 44px
  fs48: "3rem", // 48px
  fs56: "3.5rem", // 56px
  fs60: "3.75rem", // 60px
  fs64: "4rem", // 64px
  fs72: "4.5rem", // 72px
  fs80: "5rem", // 80px
  fs92: "5.75rem", // 92px
  fs116: "7.25rem", // 116px
  fs128: "8rem", // 128px
  fs140: "8.75rem", // 140px
};
// Used for padding, margin, position, gridGap etc
// see for more info https://theme-ui.com/sx-prop#theme-aware-properties
export const space = {
  0: "0",
  s25: "1px",
  s50: "2px",
  s100: "4px",
  s200: "8px",
  s300: "12px",
  s400: "16px",
  s500: "20px",
  s600: "24px",
  s700: "32px",
  s800: "40px",
  s900: "48px",
  s1000: "56px",
  s1100: "64px",
  s1200: "80px",
  s1300: "96px",
  s1400: "128px",
  s1500: "160px",
  s1600: "192px",
  s1700: "224px",
  s1800: "256px",
};

// Used for width, height, min/maxWidth, min/maxHeight etc
// see for more info https://theme-ui.com/sx-prop#theme-aware-properties
export const sizes = {
  wide: 1440,
  base: 1200,
  container: 1200, // Theme UI Default
  small: 960,
  content05: 820,
  content04: 680,
  content03: 560,
  content02: 424,
  content01: 360,

  // Special Cases
  maxContainer: 1920,
  account: 1100, // Deprecated
  trustIcons: 736, // Deprecated

  full: "100%",
  screenHeight: "100vh",
  screenWidth: "100vw",

  // new banner & navigation menu heights
  superBannerHeight: [52, 42, 42, 42, 42],
  navBarHeight: [46, 54, 54, 62, 62],
  categoryBarHeight: [0, 0, 0, 53, 53],
  navHiddenHeight: [0, 0, 0, 0, 0],

  //Icons
  iconSizeSm: "1rem", // 16px
  iconSizeBase: "1.5rem", // 24px
  iconSizeMd: "2rem", // 32px
  iconSizeLg: "2.5rem", // 40px
  iconSizeXl: "3rem", // 48px
  iconSize2xl: "4rem", // 64px
  iconSize3xl: "6.25rem", // 100px

  "1/2": "50%",
  "1/3": "33.333333%",
  "2/3": "66.666667%",
  "1/4": "25%",
  "2/4": "50%",
  "3/4": "75%",
  "1/5": "20%",
  "2/5": "40%",
  "3/5": "60%",
  "4/5": "80%",
  "1/6": "16.666667%",
  "2/6": "33.333333%",
  "3/6": "50%",
  "4/6": "66.666667%",
  "5/6": "83.333333%",
  "1/12": "8.333333%",
  "2/12": "16.666667%",
  "3/12": "25%",
  "4/12": "33.333333%",
  "5/12": "41.666667%",
  "6/12": "50%",
  "7/12": "58.333333%",
  "8/12": "66.666667%",
  "9/12": "75%",
  "10/12": "83.333333%",
  "11/12": "91.666667%",
};

export const lineHeights = {
  lh25: 0.82,
  lh50: 0.92,
  lh100: 1,
  lh200: 1.1,
  lh300: 1.3,
  lh400: 1.5,
  // Theme UI Default - Please use 100 instead.
  heading: 1,
};

export const fontWeights = {
  regular: "400",
  medium: "500",
  semibold: "600",
  bold: "700",
  black: "900",
  // Theme UI Default - Please use regular instead.
  heading: "400",
};

export const letterSpacings = {
  tight500: "-0.05em",
  tight400: "-0.02em",
  tight300: "-0.015em",
  tight200: "-0.01em",
  tight100: "-0.005em",
  normal: "0",
  loose100: "0.01em",
  loose200: "0.1em",
  loose300: "0.18em",
};

export const fonts = {
  // Base Theme ui fonts
  sans: '-apple-system, BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
  serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
  mono: 'Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace',

  // Manscaped Fonts
  heading: "'freight-big-pro-black', serif",
  textSans: "Graphik",
  brand: "'MsSans', sans-serif",
  labelButton: '"Inter", sans-serif',
};

export const radii = {
  none: "0",
  xs: "0.125rem", // 2px
  sm: "0.25rem", // 4px
  md: "0.5rem", // 8px
  lg: "1rem", // 16px
  xl: "1.5rem", // 24px
  "2xl": "2rem", // 32px
  "3xl": "2.5rem", // 40px
  rounded: "6rem", // 96px - used for rounded buttons
  full: "100%",
};

export const styles = {
  root: {
    fontFamily: "textSans",
    lineHeight: "lh400",
    fontWeight: "regular",
    fontSize: "fs16",
    MozOsxFontSmoothing: "grayscale",
    WebkitFontSmoothing: "antialiased",
  },

  buyBoxDivider: {
    color: "grey200",
    my: "s600",
  },
};

export const zIndices = {
  background: "-1",
  auto: "auto",
  0: "0",
  10: "10",
  20: "20",
  30: "30",
  40: "40",
  50: "50",
  overlay: 9999,
};

export const borderWidths = {
  light: "0.5px",
  default: "1px",
  thick: "2px",
  thicker: "3px",
  1: "1px",
  0: "0",
  2: "2px",
  4: "4px",
  8: "8px",
};

export const shadows = {
  lg: `${colors.BLACK} 0px 0px 24px -14px`,
  navProduct: `0px 0px 34px ${colors.BLACK_ALPHA_300}`,
  alert: `0px 8px 24px 0px ${colors.BLACK_ALPHA_300}`,
  elevationLow: `0px 4px 12px 0px ${colors.BLACK_ALPHA_200}`,
  elevationMid: `0px 8px 24px 0px ${colors.BLACK_ALPHA_300}`,
  elevationHigh: `0px 16px 48px 0px ${colors.BLACK_ALPHA_400}`,
};

const globalColors = {
  // Theme UI Defaults (Components without explicit styling fallback to these).
  text: colors.GREY_700,
  background: colors.WHITE,
  primary: colors.GOLD_PRIMARY,
  // Gold Colors
  goldLighter: colors.GOLD_LIGHTER,
  goldLight: colors.GOLD_LIGHT,
  goldPrimary: colors.GOLD_PRIMARY,
  goldDark: colors.GOLD_DARK,

  // Neutral Colors
  black: colors.BLACK,
  white: colors.WHITE,

  // Beige Colors
  beige50: colors.BEIGE_50,
  beige100: colors.BEIGE_100,

  // Grey Colors
  grey50: colors.GREY_50,
  grey100: colors.GREY_100,
  grey200: colors.GREY_200,
  grey300: colors.GREY_300,
  grey400: colors.GREY_400,
  grey500: colors.GREY_500,
  grey600: colors.GREY_600,
  grey700: colors.GREY_700,

  // Accent Colors
  navy: colors.NAVY,
  olive: colors.OLIVE,
  sand: colors.SAND,
  charcoal: colors.CHARCOAL,
  canvas: colors.CANVAS,

  // Alert Colors
  critical100: colors.CRITICAL_100,
  critical400: colors.CRITICAL_400,
  critical500: colors.CRITICAL_500,
  critical700: colors.CRITICAL_700,
  positive100: colors.POSITIVE_100,
  positive400: colors.POSITIVE_400,
  positive500: colors.POSITIVE_500,
  positive700: colors.POSITIVE_700,

  // Campaign Colors
  tcsPurple: colors.TCS_PURPLE,
  holidayRed: colors.HOLIDAY_RED,
  tmobilePink: colors.TMOBILE_PINK,

  // Alpha Colors
  goldPrimaryAlpha300: colors.GOLD_PRIMARY_ALPHA_300,
  goldPrimaryAlpha400: colors.GOLD_PRIMARY_ALPHA_400,
  blackAlpha100: colors.BLACK_ALPHA_100,
  blackAlpha200: colors.BLACK_ALPHA_200,
  blackAlpha300: colors.BLACK_ALPHA_300,
  blackAlpha400: colors.BLACK_ALPHA_400,
  blackAlpha500: colors.BLACK_ALPHA_500,
  blackAlpha600: colors.BLACK_ALPHA_600,
  blackAlpha700: colors.BLACK_ALPHA_700,
  blackAlpha800: colors.BLACK_ALPHA_800,
  blackAlpha900: colors.BLACK_ALPHA_900,
  whiteAlpha100: colors.WHITE_ALPHA_100,
  whiteAlpha200: colors.WHITE_ALPHA_200,
  whiteAlpha300: colors.WHITE_ALPHA_300,
  whiteAlpha400: colors.WHITE_ALPHA_400,
  whiteAlpha500: colors.WHITE_ALPHA_500,
  whiteAlpha600: colors.WHITE_ALPHA_600,
  whiteAlpha700: colors.WHITE_ALPHA_700,
  whiteAlpha800: colors.WHITE_ALPHA_800,
  whiteAlpha900: colors.WHITE_ALPHA_900,

  // Gradients
  goldFoilRight: colors.GOLD_FOIL_RIGHT,
  goldFoilBottom: colors.GOLD_FOIL_BOTTOM,
  slateTop: colors.SLATE_TOP,
  gradientBlackOverlay: colors.GRADIENT_BLACK_OVERLAY,
  gradientSlateDiagonal: colors.GRADIENT_SLATE_DIAGONAL,
  gradientSilverDiagonal: colors.GRADIENT_SILVER_DIAGONAL,
  gradientGoldDiagonal: colors.GRADIENT_GOLD_DIAGONAL,
  goldCharcoalGradient: colors.GOLD_CHARCOAL_GRADIENT,
};

const themeDefaults = {
  // removes color modes which we are not using
  useLocalStorage: false,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  letterSpacings,
  breakpoints,
  space,
  sizes,
  radii,
  zIndices,
  borderWidths,
  shadows,
  styles,

  // variants
  badges,
  buttons,
  forms,
  images,
  layout,
  links,
  modals,
  pagination,
  text,
  tooltips,
};

export const lightTheme = {
  ...themeDefaults,
  name: "THEME_UI_LIGHT",
  colors: {
    ...globalColors,
    // System Colors

    // Background Colors
    backgroundPrimary: colors.WHITE,
    backgroundLevel2: colors.BEIGE_100,
    backgroundInverse: colors.BLACK,
    backgroundBrand: colors.GOLD_PRIMARY,
    backgroundBrandLevel2: colors.GOLD_DARK,
    backgroundBrandDarkStatic: colors.GOLD_DARK,
    backgroundBrandLightStatic: colors.GOLD_LIGHT,
    backgroundNeutralAccent: colors.CANVAS,
    backgroundError: colors.CRITICAL_100,
    backgroundSuccess: colors.POSITIVE_100,
    backgroundOverlay: colors.BLACK_ALPHA_800,

    // Text Colors
    textPrimary: colors.GREY_700,
    textLevel2: colors.GREY_500,
    textLevel3: colors.GREY_400,
    textInverse: colors.WHITE,
    textBrand: colors.GOLD_PRIMARY,
    textBrandLevel2: colors.GOLD_DARK,
    textError: colors.CRITICAL_500,
    textSuccess: colors.POSITIVE_500,
    textWhiteStatic: colors.WHITE,
    textDarkStatic: colors.GREY_600,

    // Border Colors
    borderPrimary: colors.BLACK,
    borderLevel2: colors.GREY_400,
    borderLevel3: colors.GREY_200,
    borderBrand: colors.GOLD_PRIMARY,
    borderError: colors.CRITICAL_500,
    borderSuccess: colors.POSITIVE_500,

    // Interactive Colors
    interactiveNeutralDim: colors.BLACK_ALPHA_100,
    interactiveNeutralFaded: colors.BLACK_ALPHA_200,
    interactiveNeutralHighlighted: colors.BLACK_ALPHA_300,
    interactiveNeutralStrong: colors.BLACK_ALPHA_500,
  },
};

export const darkTheme = {
  ...themeDefaults,
  name: "THEME_UI_DARK",
  colors: {
    ...globalColors,
    // System Colors

    // Background Colors
    backgroundPrimary: colors.BLACK,
    backgroundLevel2: colors.GREY_700,
    backgroundInverse: colors.WHITE,
    backgroundBrand: colors.GOLD_PRIMARY,
    backgroundBrandLevel2: colors.GOLD_LIGHT,
    backgroundBrandDarkStatic: colors.GOLD_DARK,
    backgroundBrandLightStatic: colors.GOLD_LIGHT,
    backgroundNeutralAccent: colors.CHARCOAL,
    backgroundError: colors.CRITICAL_700,
    backgroundSuccess: colors.POSITIVE_700,
    backgroundOverlay: colors.BLACK_ALPHA_800,

    // Text Colors
    textPrimary: colors.WHITE,
    textLevel2: colors.GREY_200,
    textLevel3: colors.GREY_300,
    textInverse: colors.GREY_600,
    textBrand: colors.GOLD_PRIMARY,
    textBrandLevel2: colors.GOLD_LIGHT,
    textError: colors.CRITICAL_400,
    textSuccess: colors.POSITIVE_400,
    textWhiteStatic: colors.WHITE,
    textDarkStatic: colors.GREY_600,

    // Border Colors
    borderPrimary: colors.WHITE,
    borderLevel2: colors.GREY_300,
    borderLevel3: colors.GREY_500,
    borderBrand: colors.GOLD_PRIMARY,
    borderError: colors.CRITICAL_400,
    borderSuccess: colors.POSITIVE_400,

    // Interactive Colors
    interactiveNeutralDim: colors.WHITE_ALPHA_100,
    interactiveNeutralFaded: colors.WHITE_ALPHA_400,
    interactiveNeutralHighlighted: colors.WHITE_ALPHA_500,
    interactiveNeutralStrong: colors.WHITE_ALPHA_700,
  },
};
